"use client";

import { FlexColumn, FlexColumnAlignJustifyCenter, FlexRowAlignCenter } from "@/components/Flex";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import Collapse from "@mui/material/Collapse";
import { ChevronDown } from "lucide-react";
import React, { useState } from "react";
import CircularButton from "../CircularButton";
import styles from "./faq.module.css";
import clsx from "clsx";

const Ul = ({ children, style, ...ulProps }: React.HTMLAttributes<HTMLUListElement>) => {
    return (
        <ul
            {...ulProps}
            style={{ lineHeight: "2.4rem", ...style }}
            className="strippedList"
            data-testid="auto-id-be90dfaf-2899-4695-bef6-9bf87175e83a">
            {children}
        </ul>
    );
};

const Li = ({ children, style, ...liProps }: React.HTMLAttributes<HTMLLIElement>) => {
    return (
        <li
            {...liProps}
            style={{ marginBottom: "2.6rem", ...style }}
            data-testid="auto-id-a3822d8b-d69e-4473-9d80-91e691bbc7f2">
            {children}
        </li>
    );
};

const FAQ = ({
    title = "Questions & Answers",
    titleClassName,
    titleStyle,
    description = "If you have any other questions - please get in touch",
    containerStyle,
    questStyle,
    className,
    faqs,
    defaultCollapsed = false,
}: {
    title?: React.ReactNode;
    titleClassName?: string;
    titleStyle?: React.CSSProperties;
    description?: string | null;
    containerStyle?: React.CSSProperties;
    questStyle?: React.CSSProperties;
    className?: string;
    faqs: {
        question: string | React.ReactNode;
        answer: React.ReactNode[] | React.ReactNode;
    }[];
    defaultCollapsed?: boolean;
}) => {
    const [expanded, setExpanded] = useState<boolean[]>(faqs?.map(() => !defaultCollapsed));

    const toggleExpanded = (index: number) => {
        setExpanded(prev => {
            const newExpanded = [...prev];
            newExpanded[index] = !prev[index];
            return newExpanded;
        });
    };

    if (!faqs?.length) {
        return null;
    }

    return (
        <FlexColumn className={className} style={{ justifyContent: "center", ...containerStyle }}>
            <FlexColumnAlignJustifyCenter>
                <span
                    className={clsx("heading2", titleClassName)}
                    style={{ ...titleStyle }}
                    data-testid="auto-id-c6cec163-d3e9-4c33-b9a3-15c6b7dfc8d6">
                    {title}
                </span>
                {description && (
                    <span
                        className="bodyXL"
                        style={{ textAlign: "center" }}
                        data-testid="auto-id-9dd21fc6-b23a-46a2-8015-f7c0ae9e6693">
                        {description}
                    </span>
                )}
            </FlexColumnAlignJustifyCenter>
            <FlexColumn style={{ gap: spacing.MD, marginTop: spacing.LG_3 }}>
                {faqs.map((faq, index) => (
                    <FlexColumn
                        key={index}
                        onClick={() => toggleExpanded(index)}
                        style={{ cursor: "pointer", gap: spacing.SM }}>
                        <FlexRowAlignCenter style={{ justifyContent: "space-between" }}>
                            <div
                                style={{ maxWidth: "80%", fontWeight: "700", ...questStyle }}
                                data-testid="auto-id-2890163b-06a4-4c69-82a5-117c744c3a3c">{faq.question}</div>
                            <CircularButton
                                radius="3.4rem"
                                aria-label="toggle expand"
                                sx={{
                                    backgroundColor: themeColors.neutralWhite,
                                    transition: "transform 0.2s ease-in-out",
                                    transform: expanded[index] ? "rotate(180deg)" : "rotate(0deg)",
                                }}
                                tooltip={expanded[index] ? "Collapse" : "Expand"}>
                                <ChevronDown size={24} />
                            </CircularButton>
                        </FlexRowAlignCenter>
                        <Collapse className={styles.answer} in={expanded[index]}>
                            {!Array.isArray(faq.answer) ? (
                                <span data-testid="auto-id-ab1e9db2-4c0a-4767-9c9a-3a4d6e0d8cd5">{faq.answer}</span>
                            ) : (
                                <Ul data-testid="auto-id-603dd958-89c3-4bbc-b9cd-b2da0a9c72cd">
                                    {faq.answer.map((ans, idx) => {
                                        const isLast = idx === (faq.answer as React.ReactNode[]).length - 1;
                                        return (
                                            <Li
                                                key={idx}
                                                style={{ marginBottom: isLast ? 0 : "3.2rem" }}
                                                data-testid="auto-id-583cfea8-a42a-4ada-9c94-6afc092819be">
                                                {ans}
                                            </Li>
                                        );
                                    })}
                                </Ul>
                            )}
                        </Collapse>
                    </FlexColumn>
                ))}
            </FlexColumn>
        </FlexColumn>
    );
};

export default FAQ;
